// frontend/src/components/game/scorekeeper/StuartModePanel.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { apiConfig } from '../../../config/api.config';
import { generateInsights } from '../../../utils/stuartModeInsights';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

const SOLO_BADGE = {
  go:      { label: 'Solo ✓', bg: '#4CAF50', color: 'white' },
  caution: { label: 'Solo ⚠',  bg: '#F59E0B', color: 'white' },
  avoid:   { label: 'Solo ✗',  bg: '#f44336', color: 'white' },
};

const TEE_SHOT_OPTIONS = [
  { value: 'fairway', label: '🟢', title: 'Fairway' },
  { value: 'rough',   label: '🟡', title: 'Rough' },
  { value: 'bunker',  label: '🟠', title: 'Bunker' },
  { value: 'ob',      label: '🔴', title: 'OB' },
];

const StuartModePanel = ({
  players,
  currentHole,
  strokeAllocation,
  playerStandings,
  courseData,
  currentWager,
  theme,
  aiMoves = [],
}) => {
  const authFetch = useAuthenticatedFetch();
  const [teeShots, setTeeShots] = useState({});

  // Reset tee shot results whenever the hole changes
  useEffect(() => {
    setTeeShots({});
  }, [currentHole]);

  const setTeeShotResult = useCallback((playerId, result) => {
    setTeeShots(prev => ({ ...prev, [playerId]: result }));
  }, []);

  const insights = generateInsights({
    players,
    currentHole,
    strokeAllocation,
    playerStandings,
    courseData,
    currentWager,
    teeShots,
  });

  const badge = SOLO_BADGE[insights.soloRecommendation];

  // ── Commissioner state ───────────────────────────────────────────────
  const [latestIntel, setLatestIntel] = useState('');        // always-visible latest message
  const [whispererMessages, setWhispererMessages] = useState([]);
  const [whispererOpen, setWhispererOpen] = useState(false); // history/ask drawer
  const [whispererLoading, setWhispererLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const teeShotDebounceRef = useRef(null);

  // ── API helper ────────────────────────────────────────────────────────
  // messagesSnapshot must be passed explicitly by the caller so the API
  // receives the latest history even before a state re-render completes.
  const callCommissioner = useCallback(async (prompt, messagesSnapshot) => {
    setWhispererLoading(true);
    setWhispererOpen(true);
    try {
      const resp = await authFetch(`${apiConfig.baseUrl}/api/commissioner/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: prompt,
          game_state: {
            players,
            current_hole: currentHole,
            standings: playerStandings,
            stroke_allocation: strokeAllocation,
            current_wager: currentWager,
            course_data: courseData,
            whisperer_mode: true,
            tee_shots: teeShots,
            insights: {
              headline: insights.headline,
              solo_recommendation: insights.soloRecommendation,
              threats: insights.threats.map(t => ({
                name: t.player.name,
                handicap: t.player.handicap,
                threat_score: t.threatScore,
                stroke_situation: t.strokeSituation,
                tee_shot: t.teeShotResult,
                hungry: t.hungry,
                quarters: t.quarters,
              })),
            },
            conversation_history: messagesSnapshot
              .slice(-10)
              .map(m => `${m.type === 'whisperer' ? 'Commissioner' : 'Stuart'}: ${m.text}`)
              .join('\n'),
          },
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.detail || `HTTP ${resp.status}`);
      const text = json?.data?.response || 'Sorry, I could not get a response.';
      setLatestIntel(text);
      setWhispererMessages(prev => [...prev, { type: 'whisperer', text, timestamp: new Date() }]);
    } catch {
      const errText = 'Connection error — try again.';
      setLatestIntel(errText);
      setWhispererMessages(prev => [...prev, { type: 'whisperer', text: errText, timestamp: new Date() }]);
    } finally {
      setWhispererLoading(false);
    }
    // insights intentionally excluded — new object every render
  }, [players, currentHole, playerStandings, strokeAllocation, currentWager, courseData, teeShots]);

  // ── Proactive briefing on hole change ─────────────────────────────────
  useEffect(() => {
    setLatestIntel('');
    callCommissioner(
      `Give me a quick strategic briefing for hole ${currentHole}. Be direct and specific — focus on who I need to watch, whether to go solo, and any quarter context that matters. Keep it under 2 sentences.`,
      whispererMessages,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHole]);

  // ── Auto-update intel when tee shots change (debounced 1.5s) ──────────
  const prevTeeShots = useRef({});
  useEffect(() => {
    const hasShots = Object.keys(teeShots).length > 0;
    if (!hasShots) return; // ignore reset on hole change

    clearTimeout(teeShotDebounceRef.current);
    teeShotDebounceRef.current = setTimeout(() => {
      const changed = Object.entries(teeShots)
        .filter(([id, result]) => prevTeeShots.current[id] !== result)
        .map(([id, result]) => {
          const player = players.find(p => p.id === id);
          return player ? `${player.name}: ${result}` : null;
        })
        .filter(Boolean);

      prevTeeShots.current = { ...teeShots };

      if (changed.length === 0) return;

      const allShots = Object.entries(teeShots)
        .map(([id, result]) => {
          const player = players.find(p => p.id === id);
          return player ? `${player.name}: ${result}` : null;
        })
        .filter(Boolean)
        .join(', ');

      callCommissioner(
        `Tee shot update — ${allShots}. Given these results, what's the play? Go solo or partner? 1-2 sentences max.`,
        whispererMessages,
      );
    }, 1500);

    return () => clearTimeout(teeShotDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teeShots]);

  // ── Auto-scroll on new message ────────────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current?.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [whispererMessages]);

  // ── User send handler ─────────────────────────────────────────────────
  const handleSend = async () => {
    if (!inputValue.trim() || whispererLoading) return;
    const text = inputValue.trim();
    setInputValue('');
    const updatedMessages = [...whispererMessages, { type: 'user', text, timestamp: new Date() }];
    setWhispererMessages(updatedMessages);
    await callCommissioner(text, updatedMessages);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const standingsRows = [...players].sort((a, b) => {
    const qa = playerStandings?.[a.id]?.quarters ?? 0;
    const qb = playerStandings?.[b.id]?.quarters ?? 0;
    return qb - qa;
  });

  return (
    <div
      style={{
        background: theme.colors.paper,
        border: `2px solid #F59E0B`,
        borderRadius: '12px',
        marginBottom: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Strategy tip section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #92400E, #F59E0B)',
          color: 'white',
          padding: '12px 16px',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            opacity: 0.9,
            marginBottom: '8px',
          }}
        >
          🧠 Stuart Mode
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <span
            data-testid="solo-recommendation"
            style={{
              background: badge.bg,
              color: badge.color,
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              border: '2px solid rgba(255,255,255,0.4)',
            }}
          >
            {badge.label}
          </span>

          <div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '4px' }}>
              {insights.headline}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.9, lineHeight: 1.4 }}>
              {insights.detail}
            </div>
          </div>
        </div>

        {/* Commissioner intel — always visible, updates automatically */}
        {(latestIntel || whispererLoading) && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '8px',
            fontSize: '13px',
            lineHeight: 1.5,
          }}>
            {whispererLoading && !latestIntel ? (
              <span style={{ opacity: 0.7 }}>🤫 ...</span>
            ) : (
              <>
                <span style={{ opacity: 0.6, fontSize: '11px', fontWeight: 'bold' }}>🤫 </span>
                {latestIntel}
                {whispererLoading && <span style={{ opacity: 0.5 }}> ...</span>}
              </>
            )}
          </div>
        )}
      </div>

      {/* AI moves log (only when there's something to show) */}
      {aiMoves.length > 0 && (
        <div
          data-testid="ai-moves-log"
          style={{
            padding: '8px 16px',
            background: 'rgba(245,158,11,0.05)',
            borderBottom: '1px solid rgba(245,158,11,0.2)',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: theme.colors.textSecondary,
              marginBottom: '4px',
            }}
          >
            AI Moves
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {aiMoves.slice(-5).map((m, i) => (
              <div
                key={`${m.timestamp}-${i}`}
                style={{ fontSize: '12px', color: theme.colors.textPrimary }}
              >
                {m.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Standings strip */}
      <div style={{ padding: '10px 16px' }}>
        <div
          style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: theme.colors.textSecondary,
            marginBottom: '6px',
          }}
        >
          Quarter Standings
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {standingsRows.map(player => {
            const entry = insights.threats.find(t => t.player.id === player.id);
            const quarters = playerStandings?.[player.id]?.quarters ?? 0;
            const isStuart = player.is_authenticated;
            const isHungry = entry?.hungry;

            const qColor = quarters > 0 ? '#4CAF50' : quarters < 0 ? '#f44336' : '#9CA3AF';
            const qLabel = quarters > 0 ? `+${quarters}q` : `${quarters}q`;

            return (
              <div
                key={player.id}
                data-testid={`standing-${player.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  background: isStuart ? 'rgba(245,158,11,0.08)' : 'transparent',
                  border: isStuart ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent',
                }}
              >
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: isStuart ? 'bold' : '500',
                    color: theme.colors.textPrimary,
                  }}
                >
                  {player.name}
                  {isStuart && ' ←'}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isHungry && (
                    <span
                      data-testid={`hungry-${player.id}`}
                      style={{
                        fontSize: '11px',
                        color: '#f44336',
                        fontWeight: 'bold',
                      }}
                    >
                      ⚠️ hungry
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 'bold',
                      color: qColor,
                      minWidth: '40px',
                      textAlign: 'right',
                    }}
                  >
                    {qLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Tee Shot Tapper ─────────────────────────────────────────────── */}
      <div style={{
        padding: '8px 16px 10px',
        borderTop: '1px solid rgba(245,158,11,0.2)',
        background: 'rgba(245,158,11,0.03)',
      }}>
        <div style={{
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          color: theme.colors.textSecondary,
          marginBottom: '6px',
        }}>
          Tee Shots
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {players.map(player => {
            const current = teeShots[player.id] || null;
            return (
              <div
                key={player.id}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{
                  fontSize: '12px',
                  color: theme.colors.textPrimary,
                  fontWeight: player.is_authenticated ? 'bold' : '500',
                  minWidth: '70px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {player.name}
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {TEE_SHOT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      title={opt.title}
                      onClick={() => setTeeShotResult(player.id, current === opt.value ? null : opt.value)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        border: `2px solid ${current === opt.value ? '#F59E0B' : 'transparent'}`,
                        background: current === opt.value ? 'rgba(245,158,11,0.15)' : theme.colors.backgroundSecondary || 'rgba(0,0,0,0.05)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'border-color 0.1s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginTop: '4px' }}>
          🟢 Fairway · 🟡 Rough · 🟠 Bunker · 🔴 OB · tap to update threat
        </div>
      </div>

      {/* ── Ask Commissioner drawer (secondary / edge-case use) ─────────── */}
      <div style={{ borderTop: '1px solid rgba(245,158,11,0.2)' }}>

        <button
          data-testid="whisperer-toggle"
          onClick={() => setWhispererOpen(o => !o)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.colors.textSecondary,
          }}
        >
          <span style={{ fontSize: '11px' }}>Ask Commissioner</span>
          <span style={{ fontSize: '10px' }}>{whispererOpen ? '▲' : '▼'}</span>
        </button>

        {whispererOpen && (
          <>
            <div
              data-testid="whisperer-messages"
              style={{
                maxHeight: '180px',
                overflowY: 'auto',
                padding: '0 8px 4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              {whispererMessages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      background: msg.type === 'whisperer'
                        ? 'rgba(245,158,11,0.08)'
                        : 'rgba(0,0,0,0.05)',
                      borderRadius: '8px',
                      padding: '5px 9px',
                      maxWidth: '88%',
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: theme.colors.textPrimary,
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ display: 'flex', gap: '6px', padding: '6px 10px 10px' }}>
              <input
                type="text"
                placeholder="Ask something..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={whispererLoading}
                style={{
                  flex: 1,
                  border: `1px solid rgba(245,158,11,0.4)`,
                  borderRadius: '8px',
                  padding: '5px 9px',
                  fontSize: '12px',
                  outline: 'none',
                  background: 'transparent',
                  color: theme.colors.textPrimary,
                }}
              />
              <button
                data-testid="whisperer-send"
                onClick={handleSend}
                disabled={whispererLoading || !inputValue.trim()}
                style={{
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '5px 10px',
                  fontSize: '13px',
                  cursor: whispererLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                  opacity: whispererLoading || !inputValue.trim() ? 0.5 : 1,
                }}
              >
                →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

StuartModePanel.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    handicap: PropTypes.number.isRequired,
    is_authenticated: PropTypes.bool,
  })).isRequired,
  currentHole: PropTypes.number.isRequired,
  strokeAllocation: PropTypes.object,
  playerStandings: PropTypes.object,
  courseData: PropTypes.object,
  currentWager: PropTypes.number.isRequired,
  theme: PropTypes.object.isRequired,
  aiMoves: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      text: PropTypes.string.isRequired,
      timestamp: PropTypes.number,
    }),
  ),
};

export default StuartModePanel;
